import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { requestOtp, getOtp, validateOtp, validatePhoneNumber, validateOtpInput, setAuthCookies, getAccessToken } from '@/services/authService';
import { setUserProfile } from '@/utils/authStorage';
import { languageUtils } from '@/utils/languageUtils';
import type { SupportedLanguage } from '@/utils/languageUtils';
import { getAllCountries } from '@/utils/countryData';
import COUNTRY_LANGUAGES_MAP from '@/utils/countryLanguagesMap';
import { useToast } from '@/components/ui/use-toast';
import { 
  createUserProfile, 
  storeUserProfile, 
  handlePostRegistrationRefresh,
  refreshRegistrationToken,
  type RegistrationTokens 
} from '@/services/registrationAuthService';
import { ROUTE_PATHS } from '@/routes/routePaths';
import { consumePendingNavigation } from '@/utils/authPendingNavigation';

export type AuthStep = 'phone' | 'otp' | 'role';
export type UserRole = 'rider' | 'traveller' | 'both';

interface Country {
  code: string;
  name: string;
  callingCode: string;
  flag: string;
}

interface PendingTokens {
  accessToken: string;
  refreshToken: string;
  deviceId: string;
}

export interface AuthViewModelReturn {
  // State
  step: AuthStep;
  phoneNumber: string;
  countryCode: string;
  otp: string;
  selectedRole: UserRole;
  firstName: string;
  lastName: string;
  gender: string;
  referredPhoneNumber: string;
  referredCountryCode: string;
  language: SupportedLanguage;
  loading: boolean;
  error: string | null;
  otpError: string | null;
  otpShakeKey: number;
  selectedCountry: Country | null;
  referredSelectedCountry: Country | null;
  countryDropdownOpen: boolean;
  referredCountryDropdownOpen: boolean;
  countrySearch: string;
  referredCountrySearch: string;
  dropdownPosition: 'top' | 'bottom';
  filteredCountries: Country[];
  filteredReferredCountries: Country[];
  
  // Validation errors
  firstNameError: string | null;
  lastNameError: string | null;
  referredPhoneNumberError: string | null;
  
  // Actions
  setPhoneNumber: (value: string) => void;
  setCountryCode: (value: string) => void;
  setOtp: (value: string) => void;
  setSelectedRole: (role: UserRole) => void;
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  setGender: (value: string) => void;
  setReferredPhoneNumber: (value: string) => void;
  setReferredCountryCode: (value: string) => void;
  setLanguage: (lang: SupportedLanguage) => void;
  setCountryDropdownOpen: (open: boolean) => void;
  setReferredCountryDropdownOpen: (open: boolean) => void;
  setCountrySearch: (search: string) => void;
  setReferredCountrySearch: (search: string) => void;
  setSelectedCountry: (country: Country | null) => void;
  setReferredSelectedCountry: (country: Country | null) => void;
  
  // Validation handlers
  validateFirstName: (value?: string) => boolean;
  validateLastName: (value?: string) => boolean;
  validateReferredPhoneNumber: (value?: string) => boolean;
  validateAllFields: () => boolean;
  
  // Handlers
  handlePhoneSubmit: () => Promise<void>;
  handleOtpSubmit: () => Promise<void>;
  handleResendOtp: () => Promise<void>;
  handleRoleSubmit: () => Promise<void>;
  handleCountrySelect: (country: Country) => void;
  handleReferredCountrySelect: (country: Country) => void;
  
  // Refs
  countryDropdownRef: React.RefObject<HTMLDivElement>;
  referredCountryDropdownRef: React.RefObject<HTMLDivElement>;

  // Tokens issued by validate-otp (available for new users before registration completes)
  pendingTokens: PendingTokens | null;
}

export const LANGUAGE_CODE_MAP: Record<string, string> = {
  english: 'en',
  hindi: 'hi',
  telugu: 'te',
  tamil: 'ta',
  kannada: 'kn',
};

export interface AuthSuccessContext {
  userStatus: 'ExistingUser' | 'NewUser';
  phoneNumber: string;
  countryCode: string;
  data?: any;
}

export interface UseAuthViewModelOptions {
  /**
   * When provided, authentication success (existing-user verify or new-user registration)
   * invokes this callback instead of redirecting to the dashboard. Cookies/session are
   * still set, so the caller can continue an in-page flow (e.g. completing a booking).
   */
  onAuthenticated?: (context: AuthSuccessContext) => void;
}

export const useAuthViewModel = (
  mode: 'login' | 'register',
  onClose: () => void,
  options: UseAuthViewModelOptions = {}
): AuthViewModelReturn => {
  const { onAuthenticated } = options;
  const { toast } = useToast();
  
  // State
  const [step, setStep] = useState<AuthStep>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otp, setOtp] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole>('both');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [referredPhoneNumber, setReferredPhoneNumber] = useState('');
  const [referredCountryCode, setReferredCountryCode] = useState('+91');
  const [language, setLanguage] = useState<SupportedLanguage>(() => languageUtils.getCurrentLanguage());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpShakeKey, setOtpShakeKey] = useState(0);
  const [pendingTokens, setPendingTokens] = useState<PendingTokens | null>(null);
  
  // Validation error states
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [referredPhoneNumberError, setReferredPhoneNumberError] = useState<string | null>(null);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [referredCountryDropdownOpen, setReferredCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [referredCountrySearch, setReferredCountrySearch] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const referredCountryDropdownRef = useRef<HTMLDivElement>(null);

  // Country data
  const allCountries = getAllCountries('en');
  const countries: Country[] = [...allCountries, { code: 'OTHER', name: 'Other', callingCode: '', flag: '🌐' }];
  
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(() => {
    const defaultCountry = countries.find(c => c.callingCode === '+91') || countries[0];
    return COUNTRY_LANGUAGES_MAP[defaultCountry.code] ? defaultCountry : countries.find(c => c.code === 'OTHER') || null;
  });

  const [referredSelectedCountry, setReferredSelectedCountry] = useState<Country | null>(() => {
    const defaultCountry = countries.find(c => c.callingCode === '+91') || countries[0];
    return COUNTRY_LANGUAGES_MAP[defaultCountry.code] ? defaultCountry : countries.find(c => c.code === 'OTHER') || null;
  });

  // Filtered countries based on search
  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return countries;
    const search = countrySearch.trim().toLowerCase();
    const startsWith = countries.filter(c =>
      c.name.toLowerCase().startsWith(search)
    ).sort((a, b) => a.name.localeCompare(b.name));
    const includes = countries.filter(c =>
      !c.name.toLowerCase().startsWith(search) &&
      c.name.toLowerCase().includes(search)
    ).sort((a, b) => a.name.localeCompare(b.name));
    return [...startsWith, ...includes];
  }, [countrySearch, countries]);

  // Filtered referred countries based on search
  const filteredReferredCountries = useMemo(() => {
    if (!referredCountrySearch.trim()) return countries;
    const search = referredCountrySearch.trim().toLowerCase();
    const startsWith = countries.filter(c =>
      c.name.toLowerCase().startsWith(search)
    ).sort((a, b) => a.name.localeCompare(b.name));
    const includes = countries.filter(c =>
      !c.name.toLowerCase().startsWith(search) &&
      c.name.toLowerCase().includes(search)
    ).sort((a, b) => a.name.localeCompare(b.name));
    return [...startsWith, ...includes];
  }, [referredCountrySearch, countries]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
      }
      if (referredCountryDropdownRef.current && !referredCountryDropdownRef.current.contains(event.target as Node)) {
        setReferredCountryDropdownOpen(false);
      }
    }
    if (countryDropdownOpen || referredCountryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [countryDropdownOpen, referredCountryDropdownOpen]);

  // Update dropdown position on open
  useEffect(() => {
    if (countryDropdownOpen && countryDropdownRef.current) {
      const rect = countryDropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 350) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [countryDropdownOpen]);

  // Handlers
  const handlePhoneSubmit = useCallback(async () => {
    setError(null);
    setOtpError(null);
    const validationError = validatePhoneNumber(phoneNumber, countryCode);
    if (validationError) {
      toast({
        title: 'Invalid Phone Number',
        description: validationError,
        variant: 'destructive',
        duration: 3000,
      });
      return;
    }
    setLoading(true);
    try {
      const res = await requestOtp({ phoneNumber, countryCode });
      if (res.ok) {
        try {
          // Parse response to check otpType
          const responseData = await res.json();
          const otpType = responseData?.otpType;
          
          // Only fetch getOtp if otpType is NOT "Dynamic"
          if (otpType !== 'Dynamic') {
            const otpRes = await getOtp({ phoneNumber });
            if (otpRes.ok) {
              const otpText = await otpRes.text();
              setOtp(otpText.trim().slice(0, 6));
            }
          }
        } catch (parseError) {
          // If parsing fails, assume it's not Dynamic and try to get OTP
          const otpRes = await getOtp({ phoneNumber });
          if (otpRes.ok) {
            const otpText = await otpRes.text();
            setOtp(otpText.trim().slice(0, 6));
          }
        }
        setStep('otp');
      } else {
        toast({
          title: 'Failed to send OTP',
          description: 'Please try again.',
          variant: 'destructive',
          duration: 3000,
        });
      }
    } catch (e) {
      toast({
        title: 'Network Error',
        description: 'Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [phoneNumber, countryCode, toast]);

  // Helper function to normalize language code
  const normalizeLanguage = (lang: string | undefined): SupportedLanguage => {
    if (!lang) return 'en';
    const normalized = lang.toLowerCase().trim();
    // Map common variations to supported languages
    const langMap: Record<string, SupportedLanguage> = {
      'en': 'en', 'english': 'en',
      'hi': 'hi', 'hindi': 'hi',
      'te': 'te', 'telugu': 'te',
      'ta': 'ta', 'tamil': 'ta',
      'kn': 'kn', 'kannada': 'kn',
    };
    return langMap[normalized] || 'en';
  };

  const reportOtpFailure = useCallback((message: string) => {
    setOtpError(message);
    setOtpShakeKey((key) => key + 1);
  }, []);

  const handleOtpChange = useCallback(
    (value: string) => {
      const digits = value.replace(/[^0-9]/g, "");
      setOtp(digits);
      if (otpError) setOtpError(null);
    },
    [otpError]
  );

  const handleOtpSubmit = useCallback(async () => {
    setError(null);
    setOtpError(null);
    const otpValidation = validateOtpInput(otp);
    if (otpValidation) {
      reportOtpFailure(otpValidation);
      return;
    }
    setLoading(true);
    try {
      // Send countryCode as-is (with +) to match requestOtp format
      const res = await validateOtp({ 
        phoneNumber, 
        otp, 
        countryCode: countryCode, // Send as-is to maintain consistency with requestOtp
        latitude: 0, // Default to 0 if location is not available
        longitude: 0 // Default to 0 if location is not available
      });
      if (res.ok) {
        const data = res.data;
        const userStatus = data?.userStatus;
        const accessToken = data?.accessToken;
        const refreshToken = data?.refreshToken;
        const deviceId = phoneNumber;

        if (!accessToken || !refreshToken || !userStatus) {
          reportOtpFailure('Something went wrong. Please try again.');
          return;
        }

        if (userStatus === 'ExistingUser') {
          try {
            // Prepare tokens for refresh
            const registrationTokens: RegistrationTokens = {
              accessToken,
              refreshToken,
              deviceId,
            };

            // Refresh tokens for existing user
            console.log('[Auth] Refreshing tokens for existing user...');
            const refreshedTokens = await refreshRegistrationToken(registrationTokens);

            // Save refreshed tokens and device ID to cookies
            setAuthCookies({
              accessToken: refreshedTokens.accessToken,
              refreshToken: refreshedTokens.refreshToken,
              deviceId: refreshedTokens.deviceId,
            });

            console.log('[Auth] Tokens refreshed and saved successfully');

            // Persist phone + profile for returning sessions
            setUserProfile({
              phoneNumber,
              callingCode: countryCode,
              firstName: data.fn,
              lastName: data.ln,
              gender: data.g,
              userRole: data.ut,
              userId: (data.uId ?? data.userId)?.toString(),
            });

            // Handle language - check both 'lang' and 'language' fields
            if (data.lang || data.language) {
              const language = data.lang || data.language;
              const normalizedLang = normalizeLanguage(language);
              languageUtils.setLanguage(normalizedLang);
              setUserProfile({ appLanguage: normalizedLang });
            }

            // When a caller wants to keep the user in-page (e.g. continue a booking),
            // hand control back via the callback instead of navigating away.
            if (onAuthenticated) {
              onAuthenticated({
                userStatus: 'ExistingUser',
                phoneNumber,
                countryCode,
                data,
              });
            } else {
              // Check for pending navigation
              const pendingNavigation = consumePendingNavigation();
              onClose();

              if (pendingNavigation) {
                window.location.href = pendingNavigation;
              } else {
                window.location.href = ROUTE_PATHS.HOME;
              }
            }
          } catch (refreshError) {
            console.error('[Auth] Token refresh failed for existing user:', refreshError);
            reportOtpFailure(
              'Authentication failed. Please try logging in again.'
            );
            setOtp('');
            return;
          }
        } else if (userStatus === 'NewUser') {
          setUserProfile({ phoneNumber, callingCode: countryCode });
          // Save deviceId temporarily (will be saved to cookies after registration)
          // Save tokens for after registration
          setPendingTokens({ accessToken, refreshToken, deviceId });
          setStep('role');
        } else {
          reportOtpFailure('Something went wrong. Please try again.');
          setOtp('');
        }
      } else {
        reportOtpFailure('Invalid OTP. Please try again.');
      }
    } catch (e: any) {
      console.error('OTP verification error:', e);
      if (e instanceof Error && (e.message.includes('network') || e.message.includes('fetch'))) {
        reportOtpFailure('Network error. Check your connection and try again.');
      } else {
        reportOtpFailure(e?.message || 'OTP verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [otp, phoneNumber, countryCode, onClose, toast, onAuthenticated, reportOtpFailure]);

  const handleResendOtp = useCallback(async () => {
    setError(null);
    setOtpError(null);
    setLoading(true);
    try {
      const res = await requestOtp({ phoneNumber, countryCode });
      if (res.ok) {
        try {
          // Parse response to check otpType
          const responseData = await res.json();
          const otpType = responseData?.otpType;
          
          // Only fetch getOtp if otpType is NOT "Dynamic"
          if (otpType !== 'Dynamic') {
            const otpRes = await getOtp({ phoneNumber });
            if (otpRes.ok) {
              const otpText = await otpRes.text();
              setOtp(otpText.trim().slice(0, 6));
            }
          }
        } catch (parseError) {
          // If parsing fails, assume it's not Dynamic and try to get OTP
          const otpRes = await getOtp({ phoneNumber });
          if (otpRes.ok) {
            const otpText = await otpRes.text();
            setOtp(otpText.trim().slice(0, 6));
          }
        }
      } else {
        toast({
          title: 'Failed to resend OTP',
          description: 'Please try again.',
          variant: 'destructive',
          duration: 3000,
        });
      }
    } catch (e) {
      toast({
        title: 'Network Error',
        description: 'Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [phoneNumber, countryCode, toast]);

  // Validation functions
  const validateFirstName = useCallback((value?: string): boolean => {
    const name = value !== undefined ? value : firstName;
    const trimmed = name.trim();
    
    if (!trimmed) {
      setFirstNameError('First name is required');
      return false;
    }
    
    if (trimmed.length < 2) {
      setFirstNameError('First name must be at least 2 characters');
      return false;
    }
    
    if (trimmed.length > 50) {
      setFirstNameError('First name must not exceed 50 characters');
      return false;
    }
    
    // Allow letters, spaces, hyphens, and apostrophes (for names like O'Brien, Mary-Jane)
    const nameRegex = /^[a-zA-Z\s'-]+$/;
    if (!nameRegex.test(trimmed)) {
      setFirstNameError('First name can only contain letters, spaces, hyphens, and apostrophes');
      return false;
    }
    
    setFirstNameError(null);
    return true;
  }, [firstName]);

  const validateLastName = useCallback((value?: string): boolean => {
    const name = value !== undefined ? value : lastName;
    const trimmed = name.trim();
    
    if (!trimmed) {
      setLastNameError('Last name is required');
      return false;
    }
    
    if (trimmed.length < 2) {
      setLastNameError('Last name must be at least 2 characters');
      return false;
    }
    
    if (trimmed.length > 50) {
      setLastNameError('Last name must not exceed 50 characters');
      return false;
    }
    
    // Allow letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-Z\s'-]+$/;
    if (!nameRegex.test(trimmed)) {
      setLastNameError('Last name can only contain letters, spaces, hyphens, and apostrophes');
      return false;
    }
    
    setLastNameError(null);
    return true;
  }, [lastName]);

  const validateReferredPhoneNumber = useCallback((value?: string): boolean => {
    const phone = value !== undefined ? value : referredPhoneNumber;
    const trimmed = phone.trim();
    
    // Referred phone number is optional, but if provided, it must be valid
    if (!trimmed) {
      setReferredPhoneNumberError(null);
      return true; // Optional field, empty is valid
    }
    
    // Remove any non-digit characters for validation
    const digitsOnly = trimmed.replace(/\D/g, '');
    
    if (digitsOnly.length < 10) {
      setReferredPhoneNumberError('Phone number must be at least 10 digits');
      return false;
    }
    
    if (digitsOnly.length > 15) {
      setReferredPhoneNumberError('Phone number must not exceed 15 digits');
      return false;
    }
    
    // Check if referred phone number is the same as user's phone number
    const userPhoneDigits = phoneNumber.replace(/\D/g, '');
    if (digitsOnly === userPhoneDigits) {
      setReferredPhoneNumberError('Referred phone number cannot be the same as your phone number');
      return false;
    }
    
    setReferredPhoneNumberError(null);
    return true;
  }, [referredPhoneNumber, phoneNumber]);

  const validateAllFields = useCallback((): boolean => {
    const isFirstNameValid = validateFirstName();
    const isLastNameValid = validateLastName();
    const isReferredPhoneValid = validateReferredPhoneNumber();
    
    return isFirstNameValid && isLastNameValid && isReferredPhoneValid;
  }, [validateFirstName, validateLastName, validateReferredPhoneNumber]);

  // Wrapper functions for setters that clear validation errors on input
  const handleFirstNameChange = useCallback((value: string) => {
    setFirstName(value);
    if (firstNameError) {
      setFirstNameError(null);
    }
  }, [firstNameError]);

  const handleLastNameChange = useCallback((value: string) => {
    setLastName(value);
    if (lastNameError) {
      setLastNameError(null);
    }
  }, [lastNameError]);

  const handleReferredPhoneNumberChange = useCallback((value: string) => {
    // Only allow digits, limit to 15 characters
    const digits = value.replace(/[^0-9]/g, '').slice(0, 15);
    setReferredPhoneNumber(digits);
    if (referredPhoneNumberError) {
      setReferredPhoneNumberError(null);
    }
  }, [referredPhoneNumberError]);

  // Helper function to map role to API user type (ut)
  // Based on React Native mapping:
  // - 'rider' (As a Guest / Find Rides) -> 'Guest'
  // - 'traveller' (As a Host / Offer Rides) -> 'Host'
  // - 'both' -> 'Guest and Host'
  const getMappedRole = (role: UserRole): string => {
    switch (role) {
      case 'rider':
        return 'Guest';
      case 'traveller':
        return 'Host';
      case 'both':
        return 'Guest and Host';
      default:
        return 'Guest';
    }
  };

  // Helper function to format country code
  const getFormattedCountryCode = (countryCode: string): string => {
    if (countryCode && countryCode.startsWith('+')) {
      return countryCode;
    }
    if (countryCode && /^\d+$/.test(countryCode)) {
      return `+${countryCode}`;
    }
    return countryCode || '+91';
  };

  const handleRoleSubmit = useCallback(async () => {
    // Validate all fields before submission
    if (!validateAllFields()) {
      toast({
        title: 'Validation Error',
        description: 'Please correct the errors in the form before submitting.',
        variant: 'destructive',
        duration: 3000,
      });
      return;
    }

    if (!gender) {
      toast({
        title: 'Missing Information',
        description: 'Please select your gender.',
        variant: 'destructive',
        duration: 3000,
      });
      return;
    }

    if (!pendingTokens) {
      toast({
        title: 'Error',
        description: 'Registration tokens not available. Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    
    try {
      // Get language from cookie or default to 'en'
      const currentLang = languageUtils.getCurrentLanguage();
      const normalizedLang = normalizeLanguage(currentLang);
      
      // Format country code
      const formattedCountryCode = getFormattedCountryCode(countryCode);
      
      // Map role to API user type (ut)
      const mappedRole = getMappedRole(selectedRole);
      
      // Prepare registration tokens
      const registrationTokens: RegistrationTokens = {
        accessToken: pendingTokens.accessToken,
        refreshToken: pendingTokens.refreshToken,
        deviceId: pendingTokens.deviceId,
      };

      // First API call: Create user profile
      const profilePayload: any = {
        fn: firstName.trim(),
        ln: lastName.trim(),
        cc: formattedCountryCode,
        lang: normalizedLang,
        ut: mappedRole,
        g: gender || '',
      };
      
      // Add referred phone number if provided
      if (referredPhoneNumber.trim()) {
        profilePayload.refph = referredPhoneNumber.trim();
        profilePayload.refcc = referredCountryCode || countryCode;
      }

      console.log('[Registration] Creating user profile with payload:', profilePayload);
      const profileResponse = await createUserProfile(
        registrationTokens,
        profilePayload,
        normalizedLang
      );

      console.log('[Registration] Profile created successfully:', profileResponse);
      
      // Extract userId from response (check both uId and userId fields)
      const userId = typeof profileResponse.uId === 'number' ? profileResponse.uId : profileResponse.userId;
      
      if (!userId || typeof userId !== 'number') {
        throw new Error('Invalid response from server: userId not found');
      }

      setUserProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        callingCode: formattedCountryCode,
        appLanguage: normalizedLang,
        userRole: mappedRole,
        gender,
        userId: userId.toString(),
        phoneNumber,
      });

      console.log('[Registration] User data saved to persistent cookies');

      // Second API call: Store user profile (asynchronous)
      const storePayload: {
        fn: string;
        ln: string;
        lang: string;
        ut: string;
        ph: string;
        uId: number;
        cc: string;
        g: string;
        refph?: string;
        refcc?: string;
      } = {
        fn: firstName.trim(),
        ln: lastName.trim(),
        lang: normalizedLang,
        ut: mappedRole,
        ph: phoneNumber,
        uId: userId,
        cc: formattedCountryCode,
        g: gender,
      };

      // Add referred phone number and calling code if provided
      if (referredPhoneNumber.trim()) {
        storePayload.refph = referredPhoneNumber.trim();
        storePayload.refcc = referredCountryCode || countryCode;
      }

      console.log('[Registration] Storing user profile with payload:', storePayload);
      await storeUserProfile(
        registrationTokens,
        storePayload,
        normalizedLang
      );

      console.log('[Registration] User profile stored successfully');

      // Third step: Post-registration token refresh
      console.log('[Registration] Refreshing tokens after registration');
      const refreshedTokens = await handlePostRegistrationRefresh(registrationTokens);
      
      // Update pendingTokens with refreshed tokens
      setPendingTokens({
        accessToken: refreshedTokens.accessToken,
        refreshToken: refreshedTokens.refreshToken,
        deviceId: refreshedTokens.deviceId,
      });

      // Save tokens to cookies (already done in handlePostRegistrationRefresh, but ensure it's done)
      setAuthCookies({
        accessToken: refreshedTokens.accessToken,
        refreshToken: refreshedTokens.refreshToken,
        deviceId: refreshedTokens.deviceId,
      });

      toast({
        title: 'Registration Complete!',
        description: 'Your profile has been created successfully.',
        duration: 2000,
      });

      // When a caller wants to keep the user in-page (e.g. continue a booking), hand
      // control back via the callback instead of navigating away.
      if (onAuthenticated) {
        onAuthenticated({
          userStatus: 'NewUser',
          phoneNumber,
          countryCode,
          data: profileResponse,
        });
      } else {
        const pendingNavigation = consumePendingNavigation();

        // Navigate after a short delay
        setTimeout(() => {
          onClose();
          if (pendingNavigation) {
            window.location.href = pendingNavigation;
          } else {
            window.location.href = ROUTE_PATHS.HOME;
          }
        }, 2000);
      }

    } catch (e: any) {
      console.error('[Registration] Error:', e);
      toast({
        title: 'Registration Failed',
        description: e?.message || 'Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [firstName, lastName, gender, referredPhoneNumber, referredCountryCode, selectedRole, countryCode, phoneNumber, pendingTokens, onClose, toast, onAuthenticated]);

  const handleCountrySelect = useCallback((country: Country) => {
    if (!COUNTRY_LANGUAGES_MAP[country.code]) {
      const otherCountry = countries.find(c => c.code === 'OTHER');
      if (otherCountry) {
        setSelectedCountry(otherCountry);
        setCountryCode(otherCountry.callingCode || '+91');
        setLanguage('en' as SupportedLanguage);
      }
    } else {
      setSelectedCountry(country);
      setCountryCode(country.callingCode);
    }
    setCountryDropdownOpen(false);
    setCountrySearch('');
  }, [countries]);

  const handleReferredCountrySelect = useCallback((country: Country) => {
    if (!COUNTRY_LANGUAGES_MAP[country.code]) {
      const otherCountry = countries.find(c => c.code === 'OTHER');
      if (otherCountry) {
        setReferredSelectedCountry(otherCountry);
        setReferredCountryCode(otherCountry.callingCode || '+91');
      }
    } else {
      setReferredSelectedCountry(country);
      setReferredCountryCode(country.callingCode);
    }
    setReferredCountryDropdownOpen(false);
    setReferredCountrySearch('');
  }, [countries]);

  return {
    // State
    step,
    phoneNumber,
    countryCode,
    otp,
    selectedRole,
    firstName,
    lastName,
    gender,
    referredPhoneNumber,
    referredCountryCode,
    language,
    loading,
    error,
    otpError,
    otpShakeKey,
    selectedCountry,
    referredSelectedCountry,
    countryDropdownOpen,
    referredCountryDropdownOpen,
    countrySearch,
    referredCountrySearch,
    dropdownPosition,
    filteredCountries,
    filteredReferredCountries,
    
    // Validation errors
    firstNameError,
    lastNameError,
    referredPhoneNumberError,
    
    // Actions
    setPhoneNumber,
    setCountryCode,
    setOtp: handleOtpChange,
    setSelectedRole,
    setFirstName: handleFirstNameChange,
    setLastName: handleLastNameChange,
    setGender,
    setReferredPhoneNumber: handleReferredPhoneNumberChange,
    setReferredCountryCode,
    setLanguage,
    setCountryDropdownOpen,
    setReferredCountryDropdownOpen,
    setCountrySearch,
    setReferredCountrySearch,
    setSelectedCountry,
    setReferredSelectedCountry,
    
    // Validation handlers
    validateFirstName,
    validateLastName,
    validateReferredPhoneNumber,
    validateAllFields,
    
    // Handlers
    handlePhoneSubmit,
    handleOtpSubmit,
    handleResendOtp,
    handleRoleSubmit,
    handleCountrySelect,
    handleReferredCountrySelect,
    
    // Refs
    countryDropdownRef,
    referredCountryDropdownRef,

    // Tokens
    pendingTokens,
  };
};

